import { useRef } from 'react';
import { outreach } from '@round/api';
import { StringSearchParams, useUrlState } from '@round/utils';
import { DEFAULT_ORDERING, parseOrdering } from '../helpers';
import { DEFAULT_TAB, getStatusParam, parseTab } from '../components/OutreachResultsTabs/helpers';

export type UrlState = {
    tab?: string;
    /** Comma separated `Location` ids. */
    location?: string;
    ordering?: string;
};

const parseUrlParams = (searchParams: StringSearchParams<UrlState>): UrlState => ({
    tab: searchParams.tab,
    location: searchParams.location,
    ordering: searchParams.ordering,
});

export const mapToApiParams = (
    urlState: UrlState
): Pick<outreach.GetOutreachResultsParams, 'status' | 'location' | 'ordering'> => ({
    status: getStatusParam(parseTab(urlState.tab)),
    location: urlState.location,
    ordering: parseOrdering(urlState.ordering),
});

const initialState: UrlState = {
    tab: DEFAULT_TAB,
    ordering: DEFAULT_ORDERING,
};

/**
 * A loose request collects creators outside the territories it named as well, so the table opens
 * narrowed to the ones it asked for and the rest stay a click on the filter away. A strict request
 * holds no one else, so the same filter would only repeat what the request already did.
 */
const getInitialLocation = ({ locations, location_match }: outreach.OutreachFilters): string | undefined =>
    location_match === 'loose' && locations.length ? locations.join(',') : undefined;

export function useOutreachResultsUrlState(filters: outreach.OutreachFilters) {
    // The initial state only reaches the url an effect after the first render, and the first list is
    // asked for off this state before that — so until it has landed, it is read from here instead.
    const pendingInitialState = useRef<UrlState>({ ...initialState, location: getInitialLocation(filters) });

    const { url, mergeSearchParams } = useUrlState<UrlState>((urlData, { merge }) => {
        const searchParams = { ...pendingInitialState.current, ...urlData.searchParams };
        // From here on the url is the whole state, a filter cleared by hand included — which the
        // seeded location must not put back.
        pendingInitialState.current = {};

        return merge(urlData, { searchParams });
    });

    return {
        urlState: parseUrlParams({ ...pendingInitialState.current, ...url.searchParams }),
        setUrlState: mergeSearchParams,
    };
}

/** The tab is a filter too: on anything but "all" it can be the reason the table is empty. */
export const getHasActiveFilters = (urlState: UrlState) =>
    !!urlState.location || getStatusParam(parseTab(urlState.tab)) !== undefined;
